import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, AlertCircle, Sparkles, Star,
  ClipboardCheck, User, MessageSquare, Lightbulb, ChevronRight, Home,
} from "lucide-react";
import api from "@/lib/api";

const DRAFT_KEY = "edulift_feedback_draft_v1";

const STEPS = [
  { key: "participant", label: "Your info", icon: User },
  { key: "training", label: "Training", icon: ClipboardCheck },
  { key: "trainer", label: "Trainer", icon: Star },
  { key: "suggestions", label: "Suggestions", icon: Lightbulb },
  { key: "review", label: "Review", icon: CheckCircle2 },
];

const SUBJECT_OPTIONS = ["English", "Hindi", "Mathematics", "Science", "Physics", "Chemistry", "Biology", "Social Studies", "Computer Science", "Arts", "Music", "PE"];
const CLASS_OPTIONS = ["Pre-Primary", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const EXPERIENCE_OPTIONS = ["0-2 yrs", "3-5 yrs", "6-10 yrs", "11-15 yrs", "16+ yrs"];

export default function Feedback() {
  const { programId } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();

  const [questions, setQuestions] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {id, status}
  const [errors, setErrors] = useState({});

  // Restore draft
  const [form, setForm] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (cached && (Date.now() - (cached._at || 0) < 1000 * 60 * 60 * 24 * 3)) return cached;
    } catch { /* ignore malformed cache */ }
    return blankForm(programId, search);
  });

  // Fetch questions + programs
  useEffect(() => {
    api.get("/feedback/questions").then((r) => setQuestions(r.data)).catch(() => setQuestions(null));
    api.get("/programs").then((r) => setPrograms(r.data || [])).catch(() => setPrograms([]));
  }, []);

  // If URL param provides program_id, hydrate it
  useEffect(() => {
    if (programId && !form.program_id) {
      setForm((f) => ({ ...f, program_id: programId }));
    }
  }, [programId, form.program_id]);

  // Autosave draft on every change
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...form, _at: Date.now() }));
  }, [form]);

  // Set program_title on program_id change
  useEffect(() => {
    if (!form.program_id || !programs.length) return;
    const p = programs.find((x) => x.id === form.program_id);
    if (p && form.program_title !== p.title) setForm((f) => ({ ...f, program_title: p.title }));
  }, [form.program_id, programs, form.program_title]);

  const percent = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  if (done) return <ThankYou id={done.id} programTitle={form.program_title} />;
  if (!questions) return <FullPageSpinner />;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAnswer = (section, qid, v) =>
    setForm((f) => ({ ...f, [section]: { ...(f[section] || {}), [qid]: v } }));

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.participant_name?.trim()) e.participant_name = "Required";
      if (!form.email?.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
      if (!form.mobile?.trim() || !/^[+\d\s\-()]{7,}$/.test(form.mobile)) e.mobile = "Valid phone required";
      if (!form.school?.trim()) e.school = "Required";
      if (!form.program_id) e.program_id = "Select a program";
      if (!form.training_date?.trim()) e.training_date = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validateStep()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/feedback", form);
      localStorage.removeItem(DRAFT_KEY);
      setDone({ id: data.id, status: data.status });
      toast.success("Feedback submitted");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Feedback already submitted for this training. Contact the administrator if you need to update it.");
      } else {
        toast.error(err.response?.data?.detail || "Could not submit. Please retry.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="feedback-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4F46E5] mb-2">
              Training feedback · Claim certificate
            </div>
            <h1 className="font-display text-3xl font-semibold text-[#0F172A]">
              Tell us about your training
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Complete this form — approval unlocks your certificate.
            </p>
          </div>
          <Link
            to={form.program_id ? `/programs/${form.program_id}` : "/programs"}
            className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#0F172A]"
          >
            <Home className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
        {/* Stepper */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-4">
          <div className="flex items-center gap-2 mb-3" data-testid="feedback-stepper">
            {STEPS.map((s, i) => {
              const active = i === step;
              const complete = i < step;
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex items-center gap-2 flex-1 last:flex-none">
                  <div
                    className={`w-8 h-8 rounded-full grid place-items-center text-xs font-semibold transition-colors ${
                      complete ? "bg-emerald-500 text-white"
                        : active ? "bg-[#0F172A] text-white"
                          : "bg-gray-100 text-gray-500"
                    }`}
                    data-testid={`step-icon-${i}`}
                  >
                    {complete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className="hidden md:block text-xs font-medium text-gray-600 mr-3">{s.label}</div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 rounded ${complete ? "bg-emerald-500" : "bg-gray-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500"
              initial={false}
              animate={{ width: `${percent}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl bg-white border border-gray-200 shadow-sm shadow-gray-900/[0.02] p-6 sm:p-10"
          >
            {step === 0 && <StepParticipant form={form} setField={setField} errors={errors} programs={programs} />}
            {step === 1 && <StepAnswers title="Training feedback" description="Rate the training on each dimension." questions={questions.training} answers={form.training_answers} onChange={(q, v) => setAnswer("training_answers", q, v)} testidPrefix="training" />}
            {step === 2 && <StepAnswers title="Trainer feedback" description="How was the trainer's delivery?" questions={questions.trainer} answers={form.trainer_answers} onChange={(q, v) => setAnswer("trainer_answers", q, v)} testidPrefix="trainer" />}
            {step === 3 && <StepAnswers title="Suggestions" description="Help us shape future trainings." questions={questions.suggestions} answers={form.suggestions} onChange={(q, v) => setAnswer("suggestions", q, v)} testidPrefix="suggest" overallRating={form.overall_rating} onOverallChange={(v) => setField("overall_rating", v)} />}
            {step === 4 && <StepReview form={form} programs={programs} />}
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={goPrev}
            disabled={step === 0}
            data-testid="feedback-prev"
            className="inline-flex items-center gap-1.5 border border-gray-200 hover:border-gray-400 disabled:opacity-50 text-[#0F172A] text-sm font-medium px-4 py-2.5 rounded-full transition-all bg-white"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-xs text-gray-500">
            Step {step + 1} of {STEPS.length} · Autosaved
          </div>
          {step < STEPS.length - 1 ? (
            <button
              onClick={goNext}
              data-testid="feedback-next"
              className="inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all shadow-lg shadow-gray-900/10"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="feedback-submit"
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all shadow-lg shadow-emerald-900/10"
            >
              <Sparkles className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit & claim certificate"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function blankForm(programId, search) {
  return {
    program_id: programId || search.get("program") || "",
    program_title: search.get("title") || "",
    training_date: search.get("date") || "",
    trainer_name: search.get("trainer") || "",
    attendance_id: "",
    participant_name: "", age: "", gender: "",
    designation: "", school: "", district: "",
    experience: "", subjects: [], classes: [],
    mobile: "", email: "",
    training_answers: {}, trainer_answers: {}, suggestions: {},
    overall_rating: 0,
  };
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen bg-[#F9FAFB] grid place-items-center text-sm text-gray-500">
      Loading feedback form…
    </div>
  );
}

/* --------------------- STEP 1: Participant --------------------- */
function StepParticipant({ form, setField, errors, programs }) {
  return (
    <div>
      <StepHeader title="Your information" description="Details for your certificate — every field is validated." />
      <div className="mt-8 grid gap-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Full name" required value={form.participant_name} onChange={(v) => setField("participant_name", v)} error={errors.participant_name} testid="p-name" />
          <TextField label="Work email" required type="email" value={form.email} onChange={(v) => setField("email", v)} error={errors.email} testid="p-email" />
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <TextField label="Mobile" required value={form.mobile} onChange={(v) => setField("mobile", v)} error={errors.mobile} testid="p-mobile" />
          <TextField label="Age" type="number" value={form.age} onChange={(v) => setField("age", v)} testid="p-age" />
          <SelectField label="Gender" value={form.gender} onChange={(v) => setField("gender", v)} options={[{ value: "", label: "Prefer not to say" }, { value: "female", label: "Female" }, { value: "male", label: "Male" }, { value: "other", label: "Other" }]} testid="p-gender" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Designation" value={form.designation} onChange={(v) => setField("designation", v)} testid="p-designation" placeholder="e.g. Science Teacher" />
          <SelectField label="Teaching experience" value={form.experience} onChange={(v) => setField("experience", v)} options={[{ value: "", label: "Select…" }, ...EXPERIENCE_OPTIONS.map((e) => ({ value: e, label: e }))]} testid="p-experience" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="School / Organization" required value={form.school} onChange={(v) => setField("school", v)} error={errors.school} testid="p-school" />
          <TextField label="District / City" value={form.district} onChange={(v) => setField("district", v)} testid="p-district" />
        </div>
        <ChipMulti label="Subjects" values={form.subjects} onChange={(v) => setField("subjects", v)} options={SUBJECT_OPTIONS} testid="p-subjects" />
        <ChipMulti label="Classes taught" values={form.classes} onChange={(v) => setField("classes", v)} options={CLASS_OPTIONS} testid="p-classes" />

        <div className="rounded-2xl bg-gray-50 border border-gray-200 p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Training details</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField
              label="Training program"
              required
              value={form.program_id}
              onChange={(v) => setField("program_id", v)}
              options={[{ value: "", label: "Select a program…" }, ...programs.map((p) => ({ value: p.id, label: p.title }))]}
              error={errors.program_id}
              testid="p-program"
            />
            <TextField label="Training date" required value={form.training_date} onChange={(v) => setField("training_date", v)} placeholder="e.g. 5 Feb 2026" error={errors.training_date} testid="p-date" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <TextField label="Trainer name" value={form.trainer_name} onChange={(v) => setField("trainer_name", v)} testid="p-trainer" />
            <TextField label="Attendance ID (optional)" value={form.attendance_id} onChange={(v) => setField("attendance_id", v)} testid="p-attid" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------- STEP 2/3/4: Answers --------------------- */
function StepAnswers({ title, description, questions, answers, onChange, testidPrefix, overallRating, onOverallChange }) {
  return (
    <div>
      <StepHeader title={title} description={description} />
      <div className="mt-8 grid gap-6">
        {questions.map((q) => (
          <QuestionBlock
            key={q.id}
            question={q}
            value={answers[q.id]}
            onChange={(v) => onChange(q.id, v)}
            testid={`${testidPrefix}-${q.id}`}
          />
        ))}
        {onOverallChange && (
          <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-[#EEF2FF] to-emerald-50 p-5">
            <div className="text-sm font-semibold text-[#0F172A]">Overall rating</div>
            <div className="text-xs text-gray-500 mt-1">A single-line score to summarise the training.</div>
            <StarRow value={overallRating || 0} onChange={onOverallChange} testid="feedback-overall" />
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------- STEP 5: Review --------------------- */
function StepReview({ form, programs }) {
  const p = programs.find((x) => x.id === form.program_id);
  return (
    <div>
      <StepHeader title="Review & submit" description="Everything looks good? Submit to request your certificate." />
      <div className="mt-8 grid gap-4">
        <ReviewBlock
          label="Participant"
          rows={[
            ["Name", form.participant_name],
            ["Email", form.email],
            ["Mobile", form.mobile],
            ["School", form.school],
            ["District", form.district],
            ["Designation", form.designation],
            ["Experience", form.experience],
            ["Subjects", (form.subjects || []).join(", ")],
            ["Classes", (form.classes || []).join(", ")],
          ]}
        />
        <ReviewBlock
          label="Training"
          rows={[
            ["Program", p?.title || form.program_title || form.program_id],
            ["Date", form.training_date],
            ["Trainer", form.trainer_name],
            ["Attendance ID", form.attendance_id || "—"],
            ["Overall rating", form.overall_rating ? `${form.overall_rating} / 5` : "—"],
          ]}
        />
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 flex items-start gap-3">
          <ClipboardCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900 leading-relaxed">
            Once submitted, our administrator will review your feedback. On approval, your
            certificate will be generated and emailed to <b>{form.email || "your email"}</b>.
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ label, rows }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="text-xs uppercase tracking-wider text-[#4F46E5] font-semibold mb-3">{label}</div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3 border-b border-gray-50 pb-1.5">
            <dt className="text-gray-500 text-xs">{k}</dt>
            <dd className="text-[#0F172A] text-right">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/* --------------------- SHARED FIELDS --------------------- */
function StepHeader({ title, description }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4F46E5] mb-2">
        <ChevronRight className="w-3 h-3 inline mr-1 -mt-0.5" /> Step
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#0F172A]">{title}</h2>
      {description && <p className="mt-2 text-gray-600">{description}</p>}
    </div>
  );
}

function TextField({ label, required, value, onChange, placeholder, type = "text", error, testid }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </div>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full px-3.5 py-2.5 rounded-xl border bg-white focus:ring-4 focus:ring-indigo-100 outline-none text-sm transition-all ${
          error ? "border-rose-300" : "border-gray-200 focus:border-[#4F46E5]"
        }`}
      />
      {error && (
        <div className="mt-1 text-xs text-rose-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      )}
    </label>
  );
}

function SelectField({ label, required, value, onChange, options, error, testid }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </div>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`w-full px-3.5 py-2.5 rounded-xl border bg-white focus:ring-4 focus:ring-indigo-100 outline-none text-sm transition-all appearance-none ${
          error ? "border-rose-300" : "border-gray-200 focus:border-[#4F46E5]"
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.value === "" && required}>{o.label}</option>
        ))}
      </select>
      {error && (
        <div className="mt-1 text-xs text-rose-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      )}
    </label>
  );
}

function ChipMulti({ label, values, onChange, options, testid }) {
  const toggle = (o) => {
    const has = values.includes(o);
    onChange(has ? values.filter((x) => x !== o) : [...values, o]);
  };
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = values.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => toggle(o)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "bg-[#0F172A] text-white border-[#0F172A]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuestionBlock({ question, value, onChange, testid }) {
  const { type, label, options } = question;
  return (
    <div className="grid gap-3">
      <div className="text-sm font-semibold text-[#0F172A]">{label}</div>
      {type === "likert" && <StarRow value={value || 0} onChange={onChange} testid={testid} />}
      {type === "choice" && (
        <div className="flex flex-wrap gap-2" data-testid={testid}>
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${
                value === opt
                  ? "bg-[#0F172A] text-white border-[#0F172A]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
      {type === "yes_no" && (
        <div className="flex gap-2" data-testid={testid}>
          {["Yes", "No"].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${
                value === opt
                  ? opt === "Yes"
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-rose-600 text-white border-rose-600"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
      {type === "multi" && (
        <div className="flex flex-wrap gap-2" data-testid={testid}>
          {options.map((opt) => {
            const arr = Array.isArray(value) ? value : [];
            const active = arr.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() =>
                  onChange(active ? arr.filter((x) => x !== opt) : [...arr, opt])
                }
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? "bg-[#EEF2FF] text-[#4F46E5] border-indigo-200"
                    : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}
      {type === "text" && (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-[#4F46E5] focus:ring-4 focus:ring-indigo-100 outline-none text-sm min-h-24 resize-none transition-all"
          placeholder="Share your thoughts…"
        />
      )}
    </div>
  );
}

function StarRow({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1"
          aria-label={`${n} star`}
        >
          <Star
            className={`w-6 h-6 ${
              value >= n ? "fill-amber-400 text-amber-400" : "text-gray-300"
            } transition-colors`}
          />
        </button>
      ))}
      <div className="ml-2 text-xs font-medium text-gray-500">
        {value ? `${value} / 5` : "Rate"}
      </div>
    </div>
  );
}

/* --------------------- THANK YOU --------------------- */
function ThankYou({ id, programTitle }) {
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4" data-testid="feedback-thankyou">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-lg w-full rounded-3xl bg-white border border-gray-200 shadow-xl shadow-gray-900/[0.05] p-8 sm:p-12 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
          className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center"
        >
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </motion.div>
        <h1 className="mt-6 font-display text-3xl font-semibold text-[#0F172A]">
          Thank you for your feedback.
        </h1>
        <p className="mt-4 text-gray-600 leading-relaxed">
          Your certificate request for <b>{programTitle || "your training"}</b> has been submitted.
          It will be reviewed by our administrator. Once approved, your certificate will be emailed
          to your registered email address.
        </p>
        {id && (
          <div className="mt-6 inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600">
            Reference · {id.slice(0, 12).toUpperCase()}
          </div>
        )}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/programs"
            className="inline-flex items-center gap-1.5 border border-gray-200 hover:border-gray-400 text-[#0F172A] text-sm font-medium px-5 py-2.5 rounded-full transition-all bg-white"
          >
            Browse programs
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all"
          >
            <Home className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
